import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError, AxiosResponse } from 'axios'
import format from 'date-fns/format'

import {
  AvailabilityRange,
  CourtReporterWeeklyAvailability,
  courtReporterWeeklyAvailability,
  getCourtReporterWeeklyAvailabilityQueryKey,
  getReporterWeeklyAvailabilityQueryKey,
  reporterWeeklyAvailability,
  setCourtReporterWeeklyAvailability,
  setReporterWeeklyAvailability,
  SetWeeklyAvailability,
  WeeklyAvailability,
} from '@/apiClient'

export const WEEK_START_FORMAT = 'yyyy-MM-dd'

export const formatWeekStart = (weekStart: Date): string =>
  format(weekStart, WEEK_START_FORMAT)

export type AvailabilityOwner = {
  role: 'reporter' | 'court_reporter'
  id: string
}

export type OwnWeeklyAvailability =
  | WeeklyAvailability
  | CourtReporterWeeklyAvailability

const weeklyAvailabilityQueryKey = (
  owner: AvailabilityOwner,
  weekStart: string,
) =>
  owner.role === 'court_reporter'
    ? getCourtReporterWeeklyAvailabilityQueryKey(owner.id, {
        week_start: weekStart,
      })
    : getReporterWeeklyAvailabilityQueryKey(owner.id, {
        week_start: weekStart,
      })

const fetchWeeklyAvailability = (
  owner: AvailabilityOwner,
  weekStart: Date,
): Promise<AxiosResponse<OwnWeeklyAvailability>> =>
  owner.role === 'court_reporter'
    ? courtReporterWeeklyAvailability(owner.id, {
        week_start: formatWeekStart(weekStart),
      })
    : reporterWeeklyAvailability(owner.id, {
        week_start: formatWeekStart(weekStart),
      })

export const useWeeklyAvailabilityQuery = (
  owner: AvailabilityOwner | null,
  weekStart: Date,
  options?: { enabled?: boolean },
) =>
  useQuery(
    owner
      ? weeklyAvailabilityQueryKey(owner, formatWeekStart(weekStart))
      : ['weekly-availability', null],
    () => fetchWeeklyAvailability(owner!, weekStart),
    { enabled: !!owner && (options?.enabled ?? true), staleTime: 0 },
  )

export const useSetWeeklyAvailabilityMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      owner,
      data,
    }: {
      owner: AvailabilityOwner
      data: SetWeeklyAvailability
    }): Promise<AxiosResponse<OwnWeeklyAvailability>> =>
      owner.role === 'court_reporter'
        ? setCourtReporterWeeklyAvailability(owner.id, data)
        : setReporterWeeklyAvailability(owner.id, data),
    {
      onSuccess: (response, { owner, data }) => {
        queryClient.setQueryData(
          weeklyAvailabilityQueryKey(owner, data.week_start),
          response,
        )
      },
    },
  )
}

export const useFetchWeeklyAvailability = () => {
  const queryClient = useQueryClient()

  return (owner: AvailabilityOwner, weekStart: Date) =>
    queryClient.fetchQuery(
      weeklyAvailabilityQueryKey(owner, formatWeekStart(weekStart)),
      () => fetchWeeklyAvailability(owner, weekStart),
    )
}

const isValidationErrorList = (detail: unknown): detail is { msg: string }[] =>
  Array.isArray(detail) && detail.every(item => typeof item?.msg === 'string')

export const getWeeklyAvailabilityErrorMessage = (
  error: AxiosError<{ detail?: unknown }> | null | undefined,
  fallback: string,
): string => {
  const detail = error?.response?.data?.detail

  if (typeof detail === 'string' && detail) {
    return detail
  }

  if (isValidationErrorList(detail) && detail.length > 0) {
    return detail.map(item => item.msg).join(' ')
  }

  return fallback
}

export type { AvailabilityRange, WeeklyAvailability }
